import { useState } from 'react'
import {
  syncApplication,
  retryCreateCRD,
  retryApplicationSetup,
  forceDeleteApplication,
} from '@/lib/api'
import { invalidateIssuesList } from '@/lib/connect-query-invalidation'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'

export function useIssueActions() {
  const [pendingSyncAppId, setPendingSyncAppId] = useState<string | null>(null)
  const [pendingRetryCRDAppId, setPendingRetryCRDAppId] = useState<string | null>(null)
  const [pendingRetrySetupAppId, setPendingRetrySetupAppId] = useState<string | null>(null)
  const [pendingDeleteAppId, setPendingDeleteAppId] = useState<string | null>(null)

  const syncMutation = useMutationWithToast(syncApplication, {
    successTitle: 'Sync triggered',
    successMessage: 'Application sync has been initiated.',
    errorTitle: 'Failed to sync',
    invalidate: invalidateIssuesList,
  })

  const retryCRDMutation = useMutationWithToast(retryCreateCRD, {
    successTitle: 'CRD creation retried',
    successMessage: 'The ArgoCD application is being created.',
    errorTitle: 'Failed to retry',
    invalidate: invalidateIssuesList,
  })

  const retrySetupMutation = useMutationWithToast(retryApplicationSetup, {
    successTitle: 'Setup retry initiated',
    successMessage: 'Repository and AWS setup is being retried.',
    errorTitle: 'Failed to retry setup',
    invalidate: invalidateIssuesList,
  })

  const deleteMutation = useMutationWithToast(forceDeleteApplication, {
    successTitle: 'Application deleted',
    successMessage: 'The application has been removed.',
    errorTitle: 'Failed to delete',
    invalidate: invalidateIssuesList,
  })

  const sync = (appId: string) => {
    setPendingSyncAppId(appId)
    syncMutation.mutate({ id: appId }, { onSettled: () => setPendingSyncAppId(null) })
  }

  const retryCRD = (appId: string) => {
    setPendingRetryCRDAppId(appId)
    retryCRDMutation.mutate({ id: appId }, { onSettled: () => setPendingRetryCRDAppId(null) })
  }

  const retrySetup = (appId: string) => {
    setPendingRetrySetupAppId(appId)
    retrySetupMutation.mutate({ id: appId }, { onSettled: () => setPendingRetrySetupAppId(null) })
  }

  const forceDelete = (appId: string) => {
    setPendingDeleteAppId(appId)
    deleteMutation.mutate({ id: appId }, { onSettled: () => setPendingDeleteAppId(null) })
  }

  return {
    sync,
    retryCRD,
    retrySetup,
    forceDelete,
    isSyncing: (appId: string) => syncMutation.isPending && pendingSyncAppId === appId,
    isRetryingCRD: (appId: string) => retryCRDMutation.isPending && pendingRetryCRDAppId === appId,
    isRetryingSetup: (appId: string) => retrySetupMutation.isPending && pendingRetrySetupAppId === appId,
    isDeleting: (appId: string) => deleteMutation.isPending && pendingDeleteAppId === appId,
  }
}
